import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { SwipeBack } from "./lib/SwipeBack";
import { useTabSwipe } from "./lib/useTabSwipe";
import { useAuthProbe } from "./lib/auth";
import { SelectedDateProvider } from "./lib/selectedDate";
import { LocaleProvider, useLocale, apiLang, useT } from "./lib/i18n";
import { ToastProvider } from "./lib/toast";
import { ConfirmProvider } from "./lib/confirm";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { BottomTabs } from "./components/BottomTabs";
import { GetStarted } from "./pages/GetStarted";
import { Register } from "./pages/Register";
import { Forgot } from "./pages/Forgot";
import { ResetPassword } from "./pages/ResetPassword";
import { VerifyEmail } from "./pages/VerifyEmail";
import { VerifyBanner } from "./components/VerifyBanner";
import { useMe } from "./lib/useMe";
import { isNativeApp } from "./lib/native";
import { configurePurchases } from "./lib/purchases";
import { checkWelcomeBack } from "./lib/mascot";
import { Mascot } from "./components/Mascot";
import { Today } from "./pages/Today";
import { CalAI } from "./pages/CalAI";
import { Settings } from "./pages/Settings";
import { MyProfile } from "./pages/MyProfile";
import { UnitsSettings } from "./pages/UnitsSettings";
import { ActivityLog } from "./pages/ActivityLog";
import { More } from "./pages/More";
import { MoreGoals } from "./pages/MoreGoals";
import { WeightHistory } from "./pages/WeightHistory";
const Progress = React.lazy(() => import("./pages/Progress").then((m) => ({ default: m.Progress })));
const Pro = React.lazy(() => import("./pages/Pro").then((m) => ({ default: m.Pro })));
import { MorePreferences } from "./pages/MorePreferences";
import { MoreReminders } from "./pages/MoreReminders";
import { MoreUsers } from "./pages/MoreUsers";
import { AddFood } from "./pages/AddFood";

// Heavy or rarely-needed screens load on demand, keeping the core bundle
// (and therefore app launch) small: Stats pulls in the whole charting
// library, Landing/Privacy are for web visitors, Onboarding runs once.
const Landing = React.lazy(() => import("./pages/Landing").then((m) => ({ default: m.Landing })));
const Privacy = React.lazy(() => import("./pages/Privacy").then((m) => ({ default: m.Privacy })));
const Impressum = React.lazy(() => import("./pages/Impressum").then((m) => ({ default: m.Impressum })));
const Upgrade = React.lazy(() => import("./pages/Upgrade").then((m) => ({ default: m.Upgrade })));
const NativeAuthReturn = React.lazy(() => import("./pages/NativeAuthReturn").then((m) => ({ default: m.NativeAuthReturn })));
const OnboardingGate = React.lazy(() => import("./components/onboarding/OnboardingGate").then((m) => ({ default: m.OnboardingGate })));
import { api } from "./lib/api";
import { todayLocal } from "./lib/date";
import { startHealthAutoSync } from "./lib/health";
import { startOfflineSync } from "./lib/offlineQueue";
import { startResumeSignal } from "./lib/appResume";
import { OfflineBanner } from "./components/OfflineBanner";
import { useVersionGate } from "./lib/useVersionGate";
import { UpdateRequired } from "./components/UpdateRequired";

export function App() {
  const { state, refresh } = useAuthProbe();
  const updateRequired = useVersionGate();
  // Foregrounding, as one signal. Started here rather than in the shell so it
  // is live before SelectedDateProvider needs it to roll the date over.
  React.useEffect(() => startResumeSignal(), []);
  if (updateRequired) {
    return (
      <LocaleProvider>
        <UpdateRequired />
      </LocaleProvider>
    );
  }
  return (
    <LocaleProvider>
      <ToastProvider>
      <ConfirmProvider>
      <SelectedDateProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="p-8 text-fg-muted">…</div>}>
          <Routes>
            {/* Sign-in lives in the app only (owner decision): on the web,
                caloly.com is a landing page and /login simply doesn't exist.
                /reset and /verify DO stay on the web — the app's password
                and verification emails link there. */}
            <Route
              path="/login"
              element={
                state === "logged_in"
                  ? <Navigate to="/" replace />
                  : isNativeApp() ? <GetStarted onAuthed={refresh} /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="/register"
              element={
                state === "logged_in"
                  ? <Navigate to="/" replace />
                  : isNativeApp()
                    ? <SwipeBack backTo="/login"><Register onAuthed={refresh} /></SwipeBack>
                    : <Navigate to="/" replace />
              }
            />
            <Route
              path="/forgot"
              element={isNativeApp() ? <SwipeBack backTo="/login"><Forgot /></SwipeBack> : <Navigate to="/" replace />}
            />
            <Route path="/reset" element={<ResetPassword onAuthed={refresh} />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/impressum" element={<Impressum />} />
            {!isNativeApp() && <Route path="/upgrade" element={<Upgrade />} />}
            {/* OAuth handoff interstitial for pre-Universal-Link app builds. */}
            <Route path="/auth/native" element={<NativeAuthReturn />} />
            {/* Marketing landing for visitors; the app itself for signed-in users. */}
            {state !== "logged_in" && state !== "unknown" && (
              // Native app: no marketing page — straight to auth.
              <Route path="/" element={isNativeApp() ? <GetStarted onAuthed={refresh} /> : <Landing />} />
            )}
            <Route element={<ProtectedRoute state={state}><Shell /></ProtectedRoute>}>
              <Route path="/" element={<Today />} />
              <Route path="/progress" element={<Progress />} />
              <Route path="/progress/weight-history" element={<WeightHistory />} />
              <Route path="/cal" element={<CalAI />} />
              <Route path="/pro" element={<Pro />} />
              {/* Old destinations keep working; both now live in Progress. */}
              <Route path="/weight" element={<Navigate to="/progress" replace />} />
              <Route path="/stats" element={<Navigate to="/progress" replace />} />
              <Route path="/more" element={<More onLogout={refresh} />} />
              <Route path="/more/goals" element={<MoreGoals />} />
              <Route path="/more/preferences" element={<MorePreferences />} />
              <Route path="/more/reminders" element={<MoreReminders />} />
              <Route path="/more/settings" element={<Settings onLogout={refresh} />} />
              <Route path="/more/profile" element={<MyProfile />} />
              <Route path="/more/units" element={<UnitsSettings />} />
              <Route path="/more/users" element={<MoreUsersWrapper />} />
              {/* Check-in history page removed (owner: the weekly check-in on
                  Today is the feature; a history list of them served no one). */}
              <Route path="/more/check-ins" element={<Navigate to="/more" replace />} />
            </Route>
            <Route
              path="/activity"
              element={
                <ProtectedRoute state={state}>
                  <SwipeBack backTo="/"><ActivityLog /></SwipeBack>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add"
              element={
                <ProtectedRoute state={state}>
                  <SwipeBack backTo="/"><AddFood /></SwipeBack>
                </ProtectedRoute>
              }
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </SelectedDateProvider>
      </ConfirmProvider>
      </ToastProvider>
    </LocaleProvider>
  );
}

function MoreUsersWrapper() {
  const { locale } = useLocale();
  const [me, setMe] = React.useState<number | null>(null);
  React.useEffect(() => {
    api.today(todayLocal(), apiLang(locale)).then((r) => setMe(r.user.id));
  }, [locale]);
  if (me == null) return <p className="text-fg-muted pt-4">Loading…</p>;
  return <MoreUsers currentUserId={me} />;
}

const TAB_ORDER = ["/", "/progress", "/cal", "/more"];

function Shell() {
  const t = useT();
  const { me, refresh: refreshMe } = useMe();
  // First-run wizard takes priority over everything else in the shell.
  const showOnboarding = !!me && !me.onboarded;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const mainRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => { mainRef.current?.scrollTo({ top: 0 }); }, [pathname]);

  // Apple Health: background step sync on open/resume once connected.
  React.useEffect(() => startHealthAutoSync(), []);
  // Push the device's StoreKit entitlement to the server (native-only).
  React.useEffect(() => { if (me) void configurePurchases(me.id); }, [me?.id]);
  // Welcome back after a long gap — warm, and never mentions how long (§2.2).
  const [welcomeBack, setWelcomeBack] = React.useState(false);
  React.useEffect(() => { if (checkWelcomeBack()) setWelcomeBack(true); }, []);
  // Offline queue: replay saved writes when connectivity returns.
  React.useEffect(() => startOfflineSync(), []);

  // Where the edge-swipe gesture leads on sub-pages (same targets as the
  // header back chevrons). Tab-root pages don't swipe back — they swipe
  // BETWEEN tabs instead.
  // History, not a fixed parent: /more/goals is reachable from Profile AND
  // Settings, and back must return to whichever opened it. /pro left the tab
  // bar (owner, 2026-08-14) and is now a sub-page opened from Profile.
  const swipeBackTo =
    pathname.startsWith("/more/") || pathname.startsWith("/progress/") || pathname === "/pro"
      ? "history" : null;
  useTabSwipe(mainRef, {
    enabled: swipeBackTo === null,
    index: TAB_ORDER.indexOf(pathname),
    order: TAB_ORDER,
    onNavigate: navigate,
  });

  return (
    <div className="h-screen-d flex flex-col text-fg overflow-hidden">
      {/* The app's only scroller. overscroll-contain stops a bounce here from
          chaining to the document; the transparent background means a bounce
          reveals the fixed wallpaper rather than a black strip. */}
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto overscroll-contain px-6 pt-safe pb-tabbar max-w-2xl mx-auto w-full"
      >
        <SwipeBack backTo={swipeBackTo}>
          {welcomeBack && (
            <button
              onClick={() => setWelcomeBack(false)}
              className="w-full mt-4 bg-surface border border-line rounded-2xl p-3 flex items-center gap-3 text-left press"
            >
              <Mascot pose="welcoming-back" size={44} />
              <span className="flex-1 text-sm">{t("mascot.welcome_back")}</span>
              <span className="text-fg-faint" aria-hidden="true">✕</span>
            </button>
          )}
          <OfflineBanner />
          <VerifyBanner />
          <Outlet />
        </SwipeBack>
      </main>
      <BottomTabs />
      {showOnboarding && <Suspense fallback={null}><OnboardingGate me={me} onDone={refreshMe} /></Suspense>}
    </div>
  );
}
